/* Per-turn classifier dispatch for the roleplay UI.
 *
 * Replaces the Comcast-vintage meta-avatar socket per ADR 0002. Each
 * time the persona finishes responding to a producer turn, the host
 * calls `runClassifierDispatch` with the turn pair; the classifier
 * runs (mock = regex, live = backend HTTP per src/lib/grading/
 * classifier.ts), the result is mapped to CBPersonaSignals, and the
 * `onSignals` callback fires.
 *
 * Designed to be:
 *   - Mode-agnostic — accepts an injected ClassifierClient so the host
 *     doesn't need to know how mock/live resolution works (the host
 *     gets one via `getClassifier()` and passes it in).
 *   - Cancellable — an AbortSignal short-circuits `onSignals` if the
 *     host has navigated away.
 *   - Errors-soft — fetch failures don't crash the UI. The host can
 *     log via `onError` if it cares. */

import type { ClassifierClient } from '@/lib/grading/classifier';
import { personaKeyToId, type PersonaKey } from '@/constants/avatars';
import type { MilestoneTag } from '@/lib/grading/types';
import type { CBPersonaSignals } from '@/lib/codebaby';
import { classificationToSignals } from './signals';

export interface DispatchArgs {
  classifier: ClassifierClient;
  personaKey: PersonaKey;
  producerText: string;
  personaText: string;
  alreadyHit: MilestoneTag[];
  /** Ordinal index of this persona turn within the session (0-based). */
  turnIndex: number;
  /** Stable id of the producer turn this classification attaches to —
   *  used by the debrief composer to quote the turn back. */
  turnId: string;
  /** Fires on success unless aborted. */
  onSignals: (s: CBPersonaSignals) => void;
  /** Fires on classifier error or thrown exception. */
  onError?: (err: Error) => void;
  /** When aborted before resolution, neither onSignals nor onError fires. */
  signal?: AbortSignal;
}

export async function runClassifierDispatch(args: DispatchArgs): Promise<void> {
  if (args.signal?.aborted) return;
  try {
    const result = await args.classifier.classifyTurn({
      personaId: personaKeyToId(args.personaKey),
      producerText: args.producerText,
      personaText: args.personaText,
      alreadyHit: args.alreadyHit,
      turnId: args.turnId,
      turnIndex: args.turnIndex,
    });
    if (args.signal?.aborted) return;
    args.onSignals(classificationToSignals(result));
  } catch (err) {
    if (args.signal?.aborted) return;
    const error = err instanceof Error ? err : new Error(String(err));
    args.onError?.(error);
  }
}
